import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { ApiResponse, Subcategory } from '../../types';
import { API_CONFIG } from '../../config';
import { readApiResponse } from '../../lib/utils/apiResponse';

const API_URL = API_CONFIG.BASE_URL;

interface SubcategoriesState {
  items: Subcategory[];
  loading: boolean;
  error: string | null;
  pagination: {
    currentPage: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
  filterItems: Subcategory[];
  filterLoading: boolean;
  filterPagination: {
    currentPage: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
}

const initialState: SubcategoriesState = {
  items: [],
  loading: false,
  error: null,
  pagination: {
    currentPage: 1,
    pageSize: 10,
    totalCount: 0,
    totalPages: 1,
  },
  filterItems: [],
  filterLoading: false,
  filterPagination: {
    currentPage: 1,
    pageSize: 20,
    totalCount: 0,
    totalPages: 1,
  },
};

export const fetchSubcategories = createAsyncThunk(
  'api/subcategories/fetchAll',
  async (params?: { categoryId?: string; pageNumber?: number; pageSize?: number; search?: string; sortBy?: string }) => {
    const queryParams = new URLSearchParams();
    if (params?.categoryId) queryParams.append('categoryId', params.categoryId);
    if (params?.pageNumber) queryParams.append('pageNumber', params.pageNumber.toString());
    if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    
    const url = `${API_URL}/api/subcategories${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await fetch(url, {
      headers: { 'Accept': 'application/json' },
    });
    const data: ApiResponse<any> = await readApiResponse(response);
    return data.result || { data: [], pagination: {} };
  }
);

export const fetchSubcategoriesForFilter = createAsyncThunk(
  'api/subcategories/fetchForFilter',
  async (params?: { categoryId?: string; pageNumber?: number; pageSize?: number; append?: boolean }) => {
    const queryParams = new URLSearchParams();
    if (params?.categoryId) queryParams.append('categoryId', params.categoryId);
    if (params?.pageNumber) queryParams.append('pageNumber', params.pageNumber.toString());
    if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());
    
    const url = `${API_URL}/api/subcategories${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await fetch(url, {
      headers: { 'Accept': 'application/json' },
    });
    const data: ApiResponse<any> = await readApiResponse(response);
    return { 
      result: data.result || { data: [], pagination: {} },
      append: params?.append || false
    };
  }
);

export const fetchTrashedSubcategories = createAsyncThunk(
  'api/subcategories/fetchTrashed',
  async (params?: { categoryId?: string; pageNumber?: number; pageSize?: number; search?: string; sortBy?: string }) => {
    const queryParams = new URLSearchParams();
    if (params?.categoryId) queryParams.append('categoryId', params.categoryId);
    if (params?.pageNumber) queryParams.append('pageNumber', params.pageNumber.toString());
    if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);

    const token = localStorage.getItem('accessToken');
    const url = `${API_URL}/api/subcategories/trashed${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await fetch(url, {
      headers: {
        Accept: 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });
    const data: ApiResponse<any> = await readApiResponse(response);
    return data.result || { data: [], pagination: {} };
  }
);

export const createSubcategory = createAsyncThunk(
  'api/subcategories/create',
  async (payload: { name: string; categoryId: string }) => {
    const token = localStorage.getItem('accessToken');
    const response = await fetch(`${API_URL}/api/subcategories`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify(payload),
    });
    const data: ApiResponse<Subcategory> = await readApiResponse(response);
    return data.result;
  }
);

export const deleteSubcategory = createAsyncThunk('api/subcategories/delete', async (id: string) => {
  const token = localStorage.getItem('accessToken');
  await fetch(`${API_URL}/api/subcategories/${id}`, {
    method: 'DELETE',
    headers: {
      Accept: 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    },
  });
  return id;
});

export const restoreSubcategory = createAsyncThunk('api/subcategories/restore', async (id: string) => {
  const token = localStorage.getItem('accessToken');
  const response = await fetch(`${API_URL}/api/subcategories/${id}/restore`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    },
  });
  const data: ApiResponse<Subcategory> = await readApiResponse(response);
  return data.result;
});

export const forceDeleteSubcategory = createAsyncThunk('api/subcategories/forceDelete', async (id: string) => {
  const token = localStorage.getItem('accessToken');
  await fetch(`${API_URL}/api/subcategories/${id}/force`, {
    method: 'DELETE',
    headers: {
      Accept: 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    },
  });
  return id;
});

export const updateSubcategory = createAsyncThunk(
  'api/subcategories/update',
  async ({ id, name, categoryId, order }: { id: string; name: string; categoryId: string; order?: number }) => {
    const body: { name: string; categoryId: string; order?: number } = { name, categoryId };
    if (order !== undefined) {
      body.order = order;
    }

    const token = localStorage.getItem('accessToken');
    const response = await fetch(`${API_URL}/api/subcategories/${id}`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify(body),
    });
    const data: ApiResponse<Subcategory> = await readApiResponse(response);
    return data.result;
  }
);

const subcategoriesSlice = createSlice({
  name: 'subcategories',
  initialState,
  reducers: {
    resetFilterSubcategories: (state) => {
      state.filterItems = [];
      state.filterPagination = initialState.filterPagination;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSubcategories.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSubcategories.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.data || action.payload;
        state.pagination = action.payload.pagination || state.pagination;
      })
      .addCase(fetchSubcategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch';
      })
      .addCase(fetchSubcategoriesForFilter.pending, (state) => {
        state.filterLoading = true;
      })
      .addCase(fetchSubcategoriesForFilter.fulfilled, (state, action) => {
        state.filterLoading = false;
        const data = action.payload.result.data || action.payload.result;
        if (action.payload.append) {
          state.filterItems = [...state.filterItems, ...data];
        } else {
          state.filterItems = data;
        }
        state.filterPagination = action.payload.result.pagination || state.filterPagination;
      })
      .addCase(fetchSubcategoriesForFilter.rejected, (state) => {
        state.filterLoading = false;
      })
      .addCase(fetchTrashedSubcategories.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchTrashedSubcategories.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.data || action.payload;
        state.pagination = action.payload.pagination || state.pagination;
      })
      .addCase(fetchTrashedSubcategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch trashed';
      })
      .addCase(createSubcategory.fulfilled, (state, action) => {
        if (action.payload) {
          state.items.push(action.payload);
        }
      })
      .addCase(deleteSubcategory.fulfilled, (state, action) => {
        state.items = state.items.filter((item) => item.id !== action.payload);
      })
      .addCase(restoreSubcategory.fulfilled, (state, action) => {
        if (action.payload) {
          state.items = state.items.filter((item) => item.id !== action.payload!.id);
        }
      })
      .addCase(forceDeleteSubcategory.fulfilled, (state, action) => {
        state.items = state.items.filter((item) => item.id !== action.payload);
      })
      .addCase(updateSubcategory.fulfilled, (state, action) => {
        if (action.payload) {
          const index = state.items.findIndex((item) => item.id === action.payload!.id);
          if (index !== -1) {
            state.items[index] = action.payload;
          }
        }
      });
  },
});

export const { resetFilterSubcategories } = subcategoriesSlice.actions;
export default subcategoriesSlice.reducer;
